if (!customElements.get('product-sticky-button')) {
  customElements.define(
    'product-sticky-button',
    class ProductStickyButton extends HTMLElement {
      constructor() {
        super();
        this.stickyButton = this.querySelector('.product-sticky-button__submit');
        this.stickyButtonText = this.querySelector('.product-sticky-button__text');
        this.sectionId = this.dataset.sectionId;
        this.mainButtonId = `ProductSubmitButton-${this.sectionId}`;
        this.mainForm = document.querySelector(`form#product-form-${this.sectionId}`);
        this.mainButton = document.querySelector(`#${this.mainButtonId}`);
        this.cartDrawer = document.querySelector('cart-drawer');
        
        this.variantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
      }

      connectedCallback() {
        // Handle viewport and visibility first
        this.handleViewportChange();
        
        if (!this.stickyButton) {
          // If there's a coming soon message, visibility is already handled
          return;
        }
        
        if (!this.mainButton) return;

        // Sync button state on load
        this.syncButtonState();

        // Listen for variant changes
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            this.syncButtonState();
          });

          // Listen for cart updates to reset button state
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            setTimeout(() => {
              this.syncButtonState();
            }, 100);
          });
        }

        // Handle sticky button click - trigger main form submission
        this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));

        // Watch for main button state changes
        this.observeMainButton();

        // Handle cart drawer open/close
        this.handleCartDrawerVisibility();

        // Handle viewport changes on resize
        window.addEventListener('resize', this.handleViewportChange.bind(this));
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      syncButtonState() {
        if (!this.mainButton || !this.stickyButton) return;

        // Sync disabled state
        const isDisabled = this.mainButton.hasAttribute('disabled') || 
                          this.mainButton.getAttribute('aria-disabled') === 'true';
        
        if (isDisabled) {
          this.stickyButton.setAttribute('disabled', '');
          this.stickyButton.setAttribute('aria-disabled', 'true');
        } else {
          this.stickyButton.removeAttribute('disabled');
          this.stickyButton.removeAttribute('aria-disabled');
        }

        // Sync text content
        const mainButtonText = this.mainButton.querySelector('span');
        if (mainButtonText && this.stickyButtonText) {
          this.stickyButtonText.textContent = mainButtonText.textContent.trim();
        }

        // Sync loading state
        if (this.mainButton.classList.contains('loading')) {
          this.stickyButton.classList.add('loading');
          this.stickyButton.querySelector('.loading__spinner')?.classList.remove('hidden');
        } else {
          this.stickyButton.classList.remove('loading');
          this.stickyButton.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      handleStickyButtonClick(e) {
        e.preventDefault();
        
        if (this.stickyButton.hasAttribute('disabled') || 
            this.stickyButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        // Click the main button to trigger form submission
        // This ensures all handlers and validation are executed
        if (this.mainButton) {
          this.mainButton.click();
        } else if (this.mainForm) {
          // Fallback: submit the form directly
          const productFormElement = this.mainForm.closest('product-form');
          if (productFormElement) {
            const form = productFormElement.querySelector('form');
            if (form) {
              form.requestSubmit();
            }
          }
        }
      }

      observeMainButton() {
        if (!this.mainButton) return;

        // Use MutationObserver to watch for attribute and class changes
        const observer = new MutationObserver(() => {
          this.syncButtonState();
        });

        observer.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true
        });

        // Also watch for text content changes
        const textObserver = new MutationObserver(() => {
          this.syncButtonState();
        });

        const mainButtonText = this.mainButton.querySelector('span');
        if (mainButtonText) {
          textObserver.observe(mainButtonText, {
            childList: true,
            characterData: true,
            subtree: true
          });
        }
      }

      handleCartDrawerVisibility() {
        if (!this.cartDrawer) return;

        const updateVisibility = () => {
          const isCartOpen = this.cartDrawer.classList.contains('active');
          const isMobile = window.innerWidth <= 749;
          const hasComingSoon = this.querySelector('.product-sticky-button__coming-soon');

          if (isCartOpen || !isMobile || hasComingSoon) {
            this.classList.add('hidden');
          } else {
            this.classList.remove('hidden');
          }
        };

        // Watch for cart drawer class changes
        const cartObserver = new MutationObserver(updateVisibility);
        cartObserver.observe(this.cartDrawer, {
          attributes: true,
          attributeFilter: ['class']
        });

        // Initial check
        updateVisibility();
      }

      handleViewportChange() {
        const isMobile = window.innerWidth <= 749;
        const isCartOpen = this.cartDrawer?.classList.contains('active');
        const hasComingSoon = this.querySelector('.product-sticky-button__coming-soon');

        if (!isMobile || isCartOpen || hasComingSoon) {
          this.classList.add('hidden');
        } else {
          this.classList.remove('hidden');
        }
      }
    }
  );
}